"use client";

import { useCallback, useRef, useState } from "react";
import { useUser } from "@clerk/nextjs";
import { applyGamjaExamCompletionCount } from "@/hooks/useGamjaExamCompletion";
import {
  gamjaExamSlugFromPath,
  getGamjaExamSet,
  markGamjaExamSetCounted,
  startGamjaExamSet,
} from "@/lib/gamja-exam-completion";
import { recordGamjaExamCompletion } from "@/lib/server-actions/gamjaExamCompletionActions";

/**
 * 감자시험 도전 횟수 기록 (JCG-3865) — RandomProblems 가 감자시험 경로에서만 쓰는 세트 수명주기.
 *
 * - `onSetStarted`: 새 세트를 출제했을 때 호출한다. 직전과 같은 문제여도 새 도전이다.
 *   문제유지·리사이즈·백그라운드 복원은 출제(fetch)를 거치지 않으므로 호출하지 않아 같은 세트를 이어 쓴다.
 * - `onAllProblemsScored`: 세트의 모든 문제가 채점되면(RandomizedProblems) 도전 1회를 기록한다.
 *   재채점·복원된 완료 세트에서도 호출되므로 세트 기록(counted)과 진행 중 요청으로 한 번만 보낸다.
 *   기록은 이용권과 무관하게 로그인 사용자에게 남기고, 표시는 Premium 이상만 한다.
 *
 * @param enabled 기출·파이널 풀이처럼 감자시험 경로라도 세지 않을 때 false.
 */
export function useGamjaExamChallenge({
  pathname,
  storagePath,
  enabled,
}: {
  pathname: string;
  /** 세트 기록 저장 네임스페이스(RandomProblems 의 storagePath) */
  storagePath: string;
  enabled: boolean;
}) {
  const { isSignedIn } = useUser();
  const slug = enabled ? gamjaExamSlugFromPath(pathname) : null;
  // 이번 세트로 기록된 도전 횟수(하단 완료 안내용). 새 세트를 출제하면 비운다.
  const [completedCount, setCompletedCount] = useState<number | null>(null);
  // 같은 세트의 기록 요청이 진행 중이면 재채점 등으로 다시 보내지 않는다.
  const recordingSetTokenRef = useRef<string | null>(null);

  const onSetStarted = useCallback(
    (problemIds: string[]) => {
      if (!slug) return;
      setCompletedCount(null);
      startGamjaExamSet(storagePath, problemIds);
    },
    [slug, storagePath],
  );

  const recordIfComplete = useCallback(
    (scoredProblemIds: string[]) => {
      if (!slug || !isSignedIn) return;
      const set = getGamjaExamSet(storagePath, scoredProblemIds);
      if (set.counted || recordingSetTokenRef.current === set.token) return;

      recordingSetTokenRef.current = set.token;
      recordGamjaExamCompletion(slug, set.token)
        .then((result) => {
          if (!result.success) return;
          markGamjaExamSetCounted(storagePath, set.token);
          applyGamjaExamCompletionCount(slug, result.count);
          if (result.counted) setCompletedCount(result.count);
        })
        .catch((err) => {
          // 네트워크 오류 등은 다음 채점(또는 복원) 때 같은 세트 토큰으로 다시 시도한다.
          console.error("감자시험 도전 횟수 기록 실패:", err);
        })
        .finally(() => {
          if (recordingSetTokenRef.current === set.token) {
            recordingSetTokenRef.current = null;
          }
        });
    },
    [slug, isSignedIn, storagePath],
  );

  return {
    /** 감자시험 경로(표시 컴포넌트용). 감자시험이 아니면 null */
    slug,
    completedCount,
    onSetStarted,
    onAllProblemsScored: slug ? recordIfComplete : undefined,
  };
}
